import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, MenuItem, Box, Typography, FormControl, FormLabel, IconButton, Tooltip, List, ListItem, Chip, Divider, Paper } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import EuroIcon from '@mui/icons-material/Euro';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useAdmin } from '../../contexts/AdminContext';
import { useAuth } from '../../contexts/AuthContext';
import getConfig from '../../config/app-config';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import UserSelectionModal from '../../components/UserSelectionModal';
import ModalConfirm from '../../components/ModalConfirm';
import ModalEditEnrollment from '../../components/ModalEditEnrollment';
import ModalResult from '../../components/ModalResult';

const initialState = {
  name: '',
  description: '',
  venue: '',
  availableSeats: '',
  startDate: '',
  endDate: '',
  time: '',
  status: 'draft',
  minLevel: '0',
  weekdays: [],
  pricing: {
    monthly: '',
    annual1: '',
    annual3: '',
  },
  teacherId: '',
  teacher: null,
  isMembershipRequired: true,
};

// Funzione per ottenere gli iscritti al corso corrente
const getCourseEnrollments = (courseId, enrollments) => {
  if (!courseId) return [];
  return enrollments.filter(enrollment => 
    enrollment.courseId === courseId || 
    (enrollment.courseId && enrollment.courseId._id === courseId)
  );
};

// Funzione per formattare la data come '15-lug-25'
const formatDate = (date) => {
  if (!date) return 'nessuno';
  return dayjs(date).locale('it').format('DD MMM YY').toLowerCase();
};

const config = getConfig();

const ModalCourse = ({ open, onClose, mode, course, onSuccess, enrollments = [], onEnrollmentAction }) => {
  const { addCourse, updateCourse, updateEnrollment, deleteEnrollment, suspendEnrollment, unsuspendEnrollment, users, error : errorAdmin } = useAdmin();
  const { isDev } = useAuth();
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isAdd = mode === 'add';

  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [openConfirmRemove, setOpenConfirmRemove] = useState(false);
  const [openConfirmRemoveEnrollment, setOpenConfirmRemoveEnrollment] = useState(false);
  const [enrollmentToRemove, setEnrollmentToRemove] = useState(null);
  const [openEditEnrollment, setOpenEditEnrollment] = useState(false);
  const [enrollmentToEdit, setEnrollmentToEdit] = useState(null);
  const [openConfirmSuspend, setOpenConfirmSuspend] = useState(false);
  const [openConfirmUnsuspend, setOpenConfirmUnsuspend] = useState(false);
  const [enrollmentToSuspend, setEnrollmentToSuspend] = useState(null);
  const [enrollmentToUnsuspend, setEnrollmentToUnsuspend] = useState(null);
  const [resultModal, setResultModal] = useState({ open: false, message: '', severity: 'info', title: '' });

  useEffect(() => {
    if ((isEdit || isView) && course) {
      setForm({
        ...course,
        availableSeats: course.availableSeats || '',
        startDate: course.startDate ? course.startDate.slice(0, 10) : '',
        endDate: course.endDate ? course.endDate.slice(0, 10) : '',
        time: course.time || '',
        minLevel: course.minLevel || '',
        weekdays: course.weekdays || [],
        pricing: {
          monthly: course.pricing?.monthly != null ? course.pricing.monthly : '',
          annual1: course.pricing?.annual1 != null ? course.pricing.annual1 : '',
          annual3: course.pricing?.annual3 != null ? course.pricing.annual3 : '',
        },
        teacherId: course.teacherId || course.teacher?._id || '',
        teacher: course.teacher || null,
        isMembershipRequired: course.isMembershipRequired || false,
      });
    } else if (isAdd) {
      setForm(initialState);
    }
  }, [course, open, mode, isEdit, isView, isAdd]);

  useEffect(() => {
    // Carica i docenti solo se serve
    if (open && (isAdd || isEdit) && teachers.length === 0) {
      // I docenti sono già disponibili nel Context come users filtrati per ruolo
      const teacherUsers = users.filter(user => user.roles && user.roles.includes('teacher'));
      setTeachers(teacherUsers);
    }
  }, [open, isAdd, isEdit, teachers.length, users]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('pricing.')) {
      setForm((prev) => ({
        ...prev,
        pricing: {
          ...prev.pricing,
          [name.split('.')[1]]: value,
        },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectTeacher = (user) => {
    setForm((prev) => ({ ...prev, teacherId: user._id, teacher: user }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      
      // Validation for weekdays
      if (isAdd && (!form.weekdays || form.weekdays.length === 0)) {
        setError('Seleziona almeno un giorno della settimana');
        setLoading(false);
        return;
      }
      
      // Validation for time
      if (!form.time) {
        setError('L\'orario è obbligatorio');
        setLoading(false);
        return;
      }
     
      // console.log('form.startDate', form.startDate);
      const payload = {
        ...form,
        availableSeats: Number(form.availableSeats),
        startDate: form.startDate ? new Date(form.startDate) : null,
        endDate: form.endDate ? new Date(form.endDate) : null,
        time: form.time,
        pricing: {
          monthly: Number(form.pricing.monthly),
          annual1: Number(form.pricing.annual1),
          annual3: Number(form.pricing.annual3),
        },
        teacherId: form.teacherId || null,
        isMembershipRequired: Boolean(form.isMembershipRequired),
      };
      delete payload.teacher;
      
      if (isAdd) {
        const result = await addCourse(payload);
        if (result.success) {
          if (onSuccess) onSuccess();
          onClose();
          setForm(initialState);
        } else {
          // console.log(result)
          setError(errorAdmin || 'Errore durante la creazione del corso');
        }
      } else if (isEdit && course?._id) {

        const result = await updateCourse({
          _id: course._id,
          ...payload,
          teacher: form.teacher || null // Includiamo i dati dell'insegnante o null se non c'è
        });

        if (result.success) {
          if (onSuccess) onSuccess();
          onClose();
          setForm(initialState);
        } else {
          setError('Errore durante la modifica del corso');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella richiesta');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm(initialState);
    setError('');
    setOpenConfirmSuspend(false);
    setOpenConfirmUnsuspend(false);
    setEnrollmentToSuspend(null);
    setEnrollmentToUnsuspend(null);
    setResultModal({ open: false, message: '', severity: 'info', title: '' });
    onClose();
  };

  // Funzione per rimuovere un iscritto dal corso
  const handleRemoveEnrollment = async () => {
    if (!enrollmentToRemove) return;
    
    setLoading(true);
    try {
      const result = await deleteEnrollment(enrollmentToRemove._id);
      if (result.success) {
        if (onEnrollmentAction) {
          onEnrollmentAction('remove', enrollmentToRemove);
        }
        setOpenConfirmRemoveEnrollment(false);
        setEnrollmentToRemove(null);
      } else {
        setError('Errore durante la rimozione dell\'iscritto');
      }
    } catch (err) {
      setError('Errore durante la rimozione dell\'iscritto');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRemoveEnrollment = (enrollment) => {
    setEnrollmentToRemove(enrollment);
    setOpenConfirmRemoveEnrollment(true);
  };

  const handleCloseRemoveEnrollment = () => {
    setOpenConfirmRemoveEnrollment(false);
    setEnrollmentToRemove(null);
  };

  const handleOpenEditEnrollment = (enrollment) => {
    setEnrollmentToEdit(enrollment);
    setOpenEditEnrollment(true);
  };

  const handleCloseEditEnrollment = () => {
    setOpenEditEnrollment(false);
    setEnrollmentToEdit(null);
  };

  const handleOpenSuspendEnrollment = (enrollment) => {
    setEnrollmentToSuspend(enrollment);
    setOpenConfirmSuspend(true);
  };

  const handleCloseSuspendEnrollment = () => {
    setOpenConfirmSuspend(false);
    setEnrollmentToSuspend(null);
  };

  const handleSuspendEnrollment = async () => {
    if (!enrollmentToSuspend) return;

    setLoading(true);
    try {
      const result = await suspendEnrollment(enrollmentToSuspend._id);
      if (result.success) {
        if (onEnrollmentAction) {
          onEnrollmentAction('suspend', result.data || enrollmentToSuspend);
        }
      } else {
        setResultModal({
          open: true,
          message: result.error || 'Errore durante la sospensione dell\'iscritto.',
          severity: 'error',
          title: 'Errore sospensione'
        });
      }
    } catch (err) {
      setResultModal({
        open: true,
        message: err.response?.data?.message || 'Errore durante la sospensione dell\'iscritto.',
        severity: 'error',
        title: 'Errore sospensione'
      });
    } finally {
      setLoading(false);
      handleCloseSuspendEnrollment();
    }
  };

  const handleOpenUnsuspendEnrollment = (enrollment) => {
    setEnrollmentToUnsuspend(enrollment);
    setOpenConfirmUnsuspend(true);
  };

  const handleCloseUnsuspendEnrollment = () => {
    setOpenConfirmUnsuspend(false);
    setEnrollmentToUnsuspend(null);
  };

  const handleUnsuspendEnrollment = async () => {
    if (!enrollmentToUnsuspend) return;

    setLoading(true);
    try {
      const result = await unsuspendEnrollment(enrollmentToUnsuspend._id);
      if (result.success) {
        if (onEnrollmentAction) {
          onEnrollmentAction('unsuspend', result.data || enrollmentToUnsuspend);
        }
      } else {
        setResultModal({
          open: true,
          message: result.error || 'Errore durante la riattivazione dell\'iscritto.',
          severity: 'error',
          title: 'Errore riattivazione'
        });
      }
    } catch (err) {
      setResultModal({
        open: true,
        message: err.response?.data?.message || 'Errore durante la riattivazione dell\'iscritto.',
        severity: 'error',
        title: 'Errore riattivazione'
      });
    } finally {
      setLoading(false);
      handleCloseUnsuspendEnrollment();
    }
  };

  const handleCloseResultModal = () => {
    setResultModal((prev) => ({ ...prev, open: false }));
  };

  // Funzione per prepopolare i campi obbligatori in modalità dev
  const handlePrepopulateFields = () => {
    if (!isDev) return;
    
    const today = new Date();
    const nextYear = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    
    setForm(prev => ({
      ...prev,
      name: 'Corso di Prova',
      description: 'Descrizione di prova per il corso di sviluppo',
      venue: 'Sala Prova',
      availableSeats: '20',
      startDate: today.toISOString().split('T')[0],
      endDate: nextYear.toISOString().split('T')[0],
      time: '18:00',
      minLevel: '1',
      weekdays: [1, 3, 5], // Lun, Mer, Ven
      pricing: {
        monthly: '50',
        annual1: '500',
        annual3: '180',
      },
      status: 'draft',
      isMembershipRequired: true,
    }));
  };

  let title = 'Corso';
  if (isAdd) title = 'Nuovo Corso';
  if (isEdit) title = 'Modifica Corso';
  if (isView) title = 'Dettagli Corso';

  return (
    <Dialog open={open} onClose={(e, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      handleClose();
    }} maxWidth='md' fullWidth disableEscapeKeyDown>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component='form' onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label='Nome' name='name' value={form.name} onChange={handleChange} fullWidth required InputProps={{ readOnly: isView }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label='Sede' name='venue' value={form.venue} onChange={handleChange} fullWidth required InputProps={{ readOnly: isView }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                select
                label='Anno di Corso' 
                name='minLevel' 
                value={form.minLevel} 
                onChange={handleChange} 
                fullWidth 
                helperText='Opzionale - Livello minimo richiesto per iscriversi'
                InputProps={{ readOnly: isView }} 
              >
                <MenuItem value={0}>
                  <em>Nessun requisito</em>
                </MenuItem>
                <MenuItem value={1}>1° anno</MenuItem>
                <MenuItem value={2}>2° anno</MenuItem>
                <MenuItem value={3}>3° anno</MenuItem>
                <MenuItem value={4}>4° anno</MenuItem>
                <MenuItem value={5}>5° anno</MenuItem>
                <MenuItem value={6}>Oltre il 5° anno</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label='Posti Disponibili' name='availableSeats' value={form.availableSeats} onChange={handleChange} fullWidth required type='number' inputProps={{ min: 1, max: 100, readOnly: isView }} />
            </Grid>
            {isView && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  
                  <Box sx={{ p: 2, borderColor: 'grey.300', borderWidth: 1, borderStyle: 'solid', borderRadius: 2, mt: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.primary">
                      {form.isMembershipRequired ? 'Richiede tessera associativa' : 'Non richiede tessera associativa'}
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
            )}
            {!isView && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <input
                      type="checkbox"
                      id="isMembershipRequired"
                      name="isMembershipRequired"
                      checked={form.isMembershipRequired}
                      onChange={(e) => {
                        setForm(prev => ({ ...prev, isMembershipRequired: e.target.checked }));
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <label htmlFor="isMembershipRequired" style={{ cursor: 'pointer' }}>
                      Richiede tessera associativa
                    </label>
                  </Box>
                </FormControl>
              </Grid>
            )}
            <Grid size={12}>
              <TextField label='Descrizione' name='description' value={form.description} onChange={handleChange} fullWidth required multiline rows={2} InputProps={{ readOnly: isView }} />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>{isView ? <TextField label='Data Inizio' value={formatDate(form.startDate)} fullWidth InputProps={{ readOnly: true }} /> : <TextField label='Data Inizio' name='startDate' value={form.startDate} onChange={handleChange} fullWidth required type='date' InputLabelProps={{ shrink: true }} />}</Grid>
            <Grid size={{ xs: 12, sm: 4 }}>{isView ? <TextField label='Data Fine' value={formatDate(form.endDate)} fullWidth InputProps={{ readOnly: true }} /> : <TextField label='Data Fine' name='endDate' value={form.endDate} onChange={handleChange} fullWidth required type='date' InputLabelProps={{ shrink: true }} />}</Grid>
            <Grid size={{ xs: 12, sm: 4 }}>{isView ? <TextField label='Orario' value={form.time} fullWidth InputProps={{ readOnly: true }} /> : <TextField label='Orario' name='time' value={form.time} onChange={handleChange} fullWidth required type='time' InputLabelProps={{ shrink: true }} />}</Grid>
            

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label='Prezzo Mensile' name='pricing.monthly' value={form.pricing.monthly} onChange={handleChange} fullWidth required type='number' inputProps={{ min: 0, readOnly: isView }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label='Prezzo Annuale (3 rate)' name='pricing.annual3' value={form.pricing.annual3} onChange={handleChange} fullWidth required type='number' inputProps={{ min: 0, readOnly: isView }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label='Prezzo Annuale (1 rata)' name='pricing.annual1' value={form.pricing.annual1} onChange={handleChange} fullWidth required type='number' inputProps={{ min: 0, readOnly: isView }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <FormLabel component="legend">Giorni della Settimana</FormLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {[
                    { value: 1, label: 'Lun' },
                    { value: 2, label: 'Mar' },
                    { value: 3, label: 'Mer' },
                    { value: 4, label: 'Gio' },
                    { value: 5, label: 'Ven' },
                    { value: 6, label: 'Sab' },
                    { value: 0, label: 'Dom' }
                  ].map((day) => (
                    <Chip
                      key={day.value}
                      label={day.label}
                      onClick={() => {
                        if (!isView) {
                          setForm(prev => ({
                            ...prev,
                            weekdays: prev.weekdays.includes(day.value)
                              ? prev.weekdays.filter(d => d !== day.value)
                              : [...prev.weekdays, day.value]
                          }));
                        }
                      }}
                      color={form.weekdays.includes(day.value) ? 'primary' : 'default'}
                      variant={form.weekdays.includes(day.value) ? 'filled' : 'outlined'}
                      sx={{ 
                        cursor: isView ? 'default' : 'pointer',
                        opacity: isView ? 0.7 : 1
                      }}
                    />
                  ))}
                </Box>
              </FormControl>
            </Grid>
            
            <Grid container size={12}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField select label='Stato' name='status' value={form.status} onChange={handleChange} fullWidth InputProps={{ readOnly: isView }} disabled={isView}>
                  <MenuItem value='draft'>Bozza</MenuItem>
                  <MenuItem value='published'>Pubblicato</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <FormControl fullWidth variant='outlined'>
                <FormLabel component='legend' sx={{ position: 'absolute', top: 0, left: 12, background: '#fff', px: 0.5, fontSize: 14, color: 'text.secondary' }}>
                  Docente
                </FormLabel>
                <Box sx={{ p: 2, borderColor: 'grey.300', borderWidth: 1, borderStyle: 'solid', borderRadius: 2, display: 'flex', alignItems: 'center', width: '100%', minHeight: 56, mt: 1 }}>
                  {form.teacher ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant='body1' fontWeight='bold' noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1 }}>
                        {form.teacher.name} {form.teacher.surname}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                        {!isView && isEdit && (
                          <Tooltip title='Rimuovi docente'>
                            <IconButton aria-label='Rimuovi docente' color='error' onClick={() => setOpenConfirmRemove(true)}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isView && (
                          <Tooltip title='Cambia docente'>
                            <IconButton aria-label='Cambia docente' color='primary' onClick={() => setTeacherModalOpen(true)}>
                              <PersonSearchIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant='body1' color='text.secondary' fontStyle='italic' sx={{ flexGrow: 1 }}>
                        Da assegnare
                      </Typography>
                      {!isView && (
                        <Tooltip title='Seleziona docente'>
                          <IconButton aria-label='Seleziona docente' color='primary' onClick={() => setTeacherModalOpen(true)}>
                            <PersonSearchIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
              </FormControl>
            </Grid>
            
            {/* Sezione Iscritti */}
            {(isView || isEdit) && course?._id && (
              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Iscritti al Corso ({getCourseEnrollments(course?._id, enrollments).length})
                  </Typography>
                  {getCourseEnrollments(course?._id, enrollments).length > 0 ? (
                    <List sx={{ bgcolor: 'background.paper' }}>
                      {getCourseEnrollments(course?._id, enrollments).map((enrollment, index) => {
                        const user = enrollment.user || enrollment.userId;
                        const paymentStatus = enrollment.paymentStatusInfo;
                        
                        return (
                          <Paper 
                            key={enrollment._id || index} 
                            elevation={1}
                            sx={{ 
                              mb: 2, 
                              // borderRadius: 2,
                              // border: '1px solid',
                              // borderColor: 'divider',
                              '&:hover': {
                                elevation: 3,
                                borderColor: 'primary.main',
                                transform: 'translateY(-1px)',
                                transition: 'all 0.2s ease-in-out'
                              }
                            }}
                          >
                            <ListItem sx={{ py: 2, px: 3 }}>
                              {/* <ListItemAvatar>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: 'primary.main',
                                    width: 48,
                                    height: 48
                                  }}
                                >
                                  <PersonIcon />
                                </Avatar>
                              </ListItemAvatar> */}
                              
                              <Box sx={{ flexGrow: 1, mr: 2 }}>
                                {/* Header con nome e status */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {user?.name || 'N/A'} {user?.surname || 'N/A'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {enrollment.status === 'suspended' && (
                                      <Chip
                                        label='Sospeso'
                                        color='error'
                                        size='small'
                                        sx={{
                                          fontWeight: 600,
                                          '& .MuiChip-label': {
                                            px: 1.5
                                          }
                                        }}
                                      />
                                    )}
                                    {paymentStatus && (
                                      <Chip 
                                        label={paymentStatus.isEnrollmentPaid ? 'Pagato' : 'Da pagare'} 
                                        color={paymentStatus.isEnrollmentPaid ? 'success' : 'warning'} 
                                        size="small" 
                                        sx={{ 
                                          fontWeight: 600,
                                          '& .MuiChip-label': {
                                            px: 1.5
                                          }
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>

                                {/* Informazioni principali */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {user?.email || 'N/A'}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PaymentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {enrollment.paymentMethod === 'monthly' ? 'Mensile' : 
                                       enrollment.paymentMethod === 'annual1' ? 'Annuale (1 pagamento)' : 
                                       enrollment.paymentMethod === 'annual3' ? 'Annuale (3 rate)' : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      Data Iscrizione: {formatDate(enrollment.createdAt)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      A partire da: {dayjs(enrollment.startDate).format('MMMM YY')}
                                    </Typography>
                                  </Box>
                                 
                                </Box>

                                {/* Informazioni di pagamento e date */}
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexWrap: 'wrap', 
                                  gap: 2,
                                  p: 1.5,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'grey.200'
                                }}>
                                  {paymentStatus && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        Pagamenti: {paymentStatus.paymentsMade}/{enrollment.totalPayments}
                                      </Typography>
                                    </Box>
                                  )}
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      Prossimo: {formatDate(paymentStatus?.nextPaymentDate)}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EuroIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {enrollment.amount || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>

                              {isEdit && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Modifica iscrizione">
                                    <IconButton 
                                      aria-label="Modifica iscrizione" 
                                      color="primary" 
                                      onClick={() => handleOpenEditEnrollment(enrollment)}
                                      disabled={loading}
                                      sx={{
                                        '&:hover': {
                                          bgcolor: 'primary.light',
                                          color: 'primary.contrastText'
                                        }
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  {config.WITH_SUSPENDED_ENROLLMENTS && (
                                    enrollment.status === 'suspended' ? (
                                      <Tooltip title="Riattiva iscrizione">
                                        <IconButton
                                          aria-label="Riattiva iscrizione"
                                          color="success"
                                          onClick={() => handleOpenUnsuspendEnrollment(enrollment)}
                                          disabled={loading}
                                          sx={{
                                            '&:hover': {
                                              bgcolor: 'success.light',
                                              color: 'success.contrastText'
                                            }
                                          }}
                                        >
                                          <PlayCircleOutlineIcon />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Sospendi iscrizione">
                                        <IconButton
                                          aria-label="Sospendi iscrizione"
                                          color="warning"
                                          onClick={() => handleOpenSuspendEnrollment(enrollment)}
                                          disabled={loading}
                                          sx={{
                                            '&:hover': {
                                              bgcolor: 'warning.light',
                                              color: 'warning.contrastText'
                                            }
                                          }}
                                        >
                                          <PauseCircleOutlineIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )
                                  )}
                                  <Tooltip title="Rimuovi iscritto">
                                    <IconButton 
                                      aria-label="Rimuovi iscritto" 
                                      color="error" 
                                      onClick={() => handleOpenRemoveEnrollment(enrollment)}
                                      disabled={loading}
                                      sx={{
                                        '&:hover': {
                                          bgcolor: 'error.light',
                                          color: 'error.contrastText'
                                        }
                                      }}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </ListItem>
                          </Paper>
                        );
                      })}
                    </List>
                  ) : (
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">
                        Nessun iscritto a questo corso
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}
            
          </Grid>
          <UserSelectionModal open={teacherModalOpen} onClose={() => setTeacherModalOpen(false)} onSelect={handleSelectTeacher} users={teachers} />
          {error && (
            <Typography color='error' sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Chiudi
        </Button>
        {isDev && isAdd && (
          <Button onClick={handlePrepopulateFields} variant='outlined' color='secondary' disabled={loading}>
            Prepopola Campi
          </Button>
        )}
        {isAdd && (
          <Button onClick={handleSubmit} variant='contained' disabled={loading}>
            Crea
          </Button>
        )}
        {isEdit && (
          <Button onClick={handleSubmit} variant='contained' disabled={loading}>
            Salva
          </Button>
        )}
      </DialogActions>
      <ModalConfirm
        open={openConfirmRemove}
        title='Rimuovi docente'
        message='Confermi di voler rimuovere il docente da questo corso?'
        onConfirm={() => {
          setForm((prev) => ({ ...prev, teacherId: null, teacher: null }));
          setOpenConfirmRemove(false);
        }}
        onCancel={() => setOpenConfirmRemove(false)}
      />
      <ModalConfirm
        open={openConfirmRemoveEnrollment}
        title='Rimuovi iscritto'
        message={`Confermi di voler rimuovere ${enrollmentToRemove?.user?.name || enrollmentToRemove?.userId?.name || 'questo iscritto'} dal corso?`}
        onConfirm={handleRemoveEnrollment}
        onCancel={handleCloseRemoveEnrollment}
      />

      {config.WITH_SUSPENDED_ENROLLMENTS && (
        <>
          <ModalConfirm
            open={openConfirmSuspend}
            title='Sospendi iscrizione'
            message={`Confermi di voler sospendere ${enrollmentToSuspend?.user?.name || enrollmentToSuspend?.userId?.name || 'questo iscritto'} dal corso?`}
            onConfirm={handleSuspendEnrollment}
            onCancel={handleCloseSuspendEnrollment}
          />

          <ModalConfirm
            open={openConfirmUnsuspend}
            title='Riattiva iscrizione'
            message={`Confermi di voler riattivare ${enrollmentToUnsuspend?.user?.name || enrollmentToUnsuspend?.userId?.name || 'questo iscritto'}?`}
            onConfirm={handleUnsuspendEnrollment}
            onCancel={handleCloseUnsuspendEnrollment}
          />
        </>
      )}

      <ModalResult
        open={resultModal.open}
        onClose={handleCloseResultModal}
        message={resultModal.message}
        severity={resultModal.severity}
        title={resultModal.title}
      />
      
      {/* Modal per modificare enrollment */}
      {enrollmentToEdit && (
        <ModalEditEnrollment
          open={openEditEnrollment}
          onClose={handleCloseEditEnrollment}
          enrollment={enrollmentToEdit}
          course={course}
          onSuccess={() => {
            if (onSuccess) onSuccess();
            handleCloseEditEnrollment();
          }}
          updateEnrollment={updateEnrollment}
        />
      )}
    </Dialog>
  );
};

export default ModalCourse;
